/**
 * Tolerant section search for the spec editor. The old filter was a raw substring
 * match against the section's Markdown, so "device description" missed a heading
 * written as "**Device** Description" or "Device  description", and a two-word query
 * had to appear verbatim. This normalises both sides (case, Markdown punctuation,
 * whitespace) and matches every word of the query anywhere in the section, in any
 * order. Pure — unit-tested beside it.
 */

// The generic half lives in lib/textSearch — the vault's folder search needed exactly this, and
// two boxes searching the same documents must not disagree about what a query means. Re-exported
// so this module stays the one place the spec editor imports from.
export { normalizeForSearch, searchTerms } from '@/lib/textSearch';
import { matchesAllTerms, normalizeForSearch, searchTerms } from '@/lib/textSearch';

/** True when EVERY query word occurs somewhere in the (normalised) section text. */
export function sectionMatches(sectionMarkdown: string, query: string): boolean {
  return matchesAllTerms(sectionMarkdown, query);
}

/** The section's heading line (the `## …` first line) without the marks; '' when the
 *  section has no heading (the front-matter block). */
export function headingText(sectionMarkdown: string): string {
  const first = sectionMarkdown.trimStart().split('\n', 1)[0] ?? '';
  return first.startsWith('#') ? first.replace(/^#+\s*/, '') : '';
}

/** True when every query word occurs in the section's HEADING — used to rank
 *  title hits above sections that merely mention the words in their body. */
export function headingMatches(sectionMarkdown: string, query: string): boolean {
  const terms = searchTerms(query);
  if (terms.length === 0) return true;
  const head = normalizeForSearch(headingText(sectionMarkdown));
  return head.length > 0 && terms.every((t) => head.includes(t));
}
